/**
 * @fileoverview Field formatters / labels for the contacts change-history feed.
 *
 * Used by `ContactDetailPanel` (and the firm-contact detail view) to render
 * before/after diffs from `previousValues` and `newValues` in human-readable
 * form. Mirrors the resolution logic the panel uses for the live row so the
 * history reads like a journal of the visible UI.
 */

import { CONTACT_TYPE_META, FIRM_CATEGORY_META } from "./types";

const fmtCurrency = (v: number): string =>
  v.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  });

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  merged: "Merged",
  archived: "Archived",
  deleted: "Deleted",
};

const formatAddressList = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) return "\u2014";
  return (
    value
      .map((addr) => {
        if (!addr || typeof addr !== "object") return "";
        const a = addr as Record<string, unknown>;
        const parts = [
          a.line1,
          a.line2,
          a.suburb,
          [a.state, a.postcode].filter(Boolean).join(" ").trim() || undefined,
          a.country,
        ]
          .filter(
            (part): part is string =>
              typeof part === "string" && part.trim().length > 0,
          )
          .map((part) => part.trim());
        return parts.join(", ");
      })
      .filter((line) => line.length > 0)
      .join(" \u2022 ") || "\u2014"
  );
};

export const CONTACT_FIELD_FORMATTERS: Record<
  string,
  (value: unknown) => string
> = {
  displayName: (v) => String(v ?? "\u2014"),
  email: (v) => String(v ?? "\u2014"),
  phone: (v) => String(v ?? "\u2014"),
  abn: (v) => String(v ?? "\u2014"),
  acn: (v) => String(v ?? "\u2014"),
  notes: (v) => String(v ?? "\u2014"),
  website: (v) => String(v ?? "\u2014"),
  contactType: (v) =>
    CONTACT_TYPE_META[v as keyof typeof CONTACT_TYPE_META]?.label ??
    String(v ?? "\u2014"),
  category: (v) =>
    FIRM_CATEGORY_META[v as keyof typeof FIRM_CATEGORY_META]?.label ??
    String(v ?? "\u2014"),
  status: (v) => STATUS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  exposure: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  addresses: formatAddressList,
  dateOfBirth: (v) => String(v ?? "\u2014"),
};

export const CONTACT_FIELD_LABELS: Record<string, string> = {
  displayName: "Name",
  dateOfBirth: "Date of birth",
  contactType: "Contact type",
  category: "Firm category",
  email: "Email",
  phone: "Phone",
  abn: "ABN",
  acn: "ACN",
  website: "Website",
  notes: "Notes",
  addresses: "Addresses",
  status: "Status",
  exposure: "Exposure",
  firmContactId: "Firm link",
  mergedIntoId: "Merged into",
  enrichmentMeta: "Enrichment metadata",
};
